import { Avatar } from "@/utils/ui/Avatar/Avatar";
import { displayName, InputComponentProps } from "./Filters";
import { Display_Value_Key as dvk } from "../Create/utils";

import { useEffect, useMemo, useState } from "react";

// Multiselect input component
const RenderMultiSelect: React.FC<InputComponentProps> = ({
  filter,
  options = [],
  onChange,
  preSelected,
  onApplyStateChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedName, setSelectedName] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");

  const filteredOptions = useMemo(() => {
    input === "" ? options : options.filter((o) => o.value.includes(input));
    if (input === "") return options;
    else {
      return options.filter((o) =>
        o.label.toLowerCase().includes(input.toLowerCase())
      );
    }
  }, [input, options]);

  const toggleSelection = (value: string) => {
    setSelectedOptions((prev) => {
      if (prev.includes(value))
        return prev.filter((option) => option !== value);
      return [...prev, value];
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleOnchange = (event: any, name: string) => {
    toggleSelection(event.target.value);
    setSelectedName((prev) => {
      if (prev.includes(name)) return prev.filter((option) => option !== name);
      return [...prev, name];
    });
  };

  // Handle select all functionality
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all filtered options
      const allFilteredValues = filteredOptions.map((option) => option.value);
      const allFilteredNames = filteredOptions.map((option) => option.label);

      setSelectedOptions(allFilteredValues);
      setSelectedName(allFilteredNames);
    } else {
      // Deselect all
      setSelectedOptions([]);
      setSelectedName([]);
    }
  };

  // Handle select only functionality
  const handleSelectOnly = (value: string, name: string) => {
    setSelectedOptions([value]);
    setSelectedName([name]);
  };

  // Check if all filtered options are selected
  const areAllFilteredSelected =
    filteredOptions.length > 0 &&
    selectedOptions.length > 0 &&
    filteredOptions?.every((option) => selectedOptions?.includes(option.value));

  // Check if some filtered options are selected (for indeterminate state)
  const areSomeFilteredSelected =
    filteredOptions?.length > 0 &&
    selectedOptions?.length > 0 &&
    filteredOptions?.some((option) => selectedOptions?.includes(option.value));

  useEffect(() => {
    if (preSelected[filter.name]) {
      setSelectedOptions(preSelected[filter.name]);
      setSelectedName(preSelected[filter.name + dvk]);
    }
  }, []);

  // Update apply state whenever selectedOptions changes
  useEffect(() => {
    if (onApplyStateChange) {
      onApplyStateChange(selectedOptions.length === 0, handleApply, "Apply");
    }
  }, [selectedOptions, onApplyStateChange]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && selectedOptions?.length > 0) {
      handleApply();
    }
  };

  const handleApply = () => {
    if (selectedOptions.length > 0) {
      onChange(filter.name, selectedOptions, selectedName);
    }
  };

  return (
    <div className="px-3">
      <div className="mb-3 py-3 border-bottom">
        {/* Select All Checkbox */}
        <div className="border-bottom pb-2">
          <input
            className="form-control form-control-sm"
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder="search"
          />
        </div>
        <div style={{ maxHeight: "250px", overflow: "auto" }} className="px-2">
          {filteredOptions.length > 0 && (
            <div className="form-check d-flex gap-1 mt-3">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="select-all-checkbox"
                checked={areAllFilteredSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate =
                      areSomeFilteredSelected && !areAllFilteredSelected;
                  }
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <label
                className="form-check-label d-flex align-items-center gap-2 text-capitalize text-truncate text-nowrap"
                htmlFor="select-all-checkbox"
                style={{ maxWidth: "70%" }}
              >
                Select All
              </label>
            </div>
          )}
          {filteredOptions.map((option, index) => (
            <div
              className="form-check align-items-center mt-3 d-flex gap-1 position-relative"
              key={index}
              onMouseEnter={(e) => {
                const button = e.currentTarget.querySelector(
                  ".select-only-btn"
                ) as HTMLElement;
                if (button) button.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                const button = e.currentTarget.querySelector(
                  ".select-only-btn"
                ) as HTMLElement;
                if (button) button.style.opacity = "0";
              }}
            >
              <input
                className="form-check-input me-2"
                type="checkbox"
                id={`checkbox-${index}`}
                value={option.value}
                onKeyDown={handleKeyPress}
                checked={selectedOptions?.includes(option.value)}
                onChange={(e) => handleOnchange(e, option.label)}
              />
              <label
                className="form-check-label d-flex align-items-center gap-2 text-capitalize "
                htmlFor={`checkbox-${index}`}
                style={{ maxWidth: "70%" }}
              >
                {["Name", "Agency", "Team/Property", "Business"].includes(
                  filter.displayName
                ) && (
                  <Avatar
                    text={option.label}
                    logo={option?.logo}
                    baseURL={option?.baseURL}
                  />
                )}
                <span className="text-truncate text-nowrap">
                  {option.label}
                </span>
              </label>

              {/* Select Only Button - visible on hover */}
              <button
                className="btn btn-sm btn-outline-secondary select-only-btn"
                // className="select-only-btn bg-light"
                style={{
                  // padding: '0.25rem 0.5rem',
                  // lineHeight: '1',
                  opacity: 0,
                }}
                onClick={() => handleSelectOnly(option.value, option.label)}
                title="Select only this option"
              >
                Only
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* <div className="d-flex justify-content-end">
        <button
          className="btn btn-primary btn-sm text-white"
          disabled={selectedOptions.length === 0}
          onClick={handleApply}
        >
          Apply
        </button>
      </div> */}
    </div>
  );
};

export default RenderMultiSelect;
